import type React from "react";
import { memo, useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Pill } from "@/components/podkit/status/Pill";
import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { useSearchRepositoriesQuery } from "@/queries/runner-queries";
import { CornerDownLeft } from "lucide-react";
import { withGitpodSize } from "@/assets/icons/utils";
import { SearchMode, type SearchRepositoriesResponse_Repository } from "gitpod-next-api/gitpod/v1/runner_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import { getScmDisplayName } from "@/components/automations/scm-utils";
import { useEnvironmentClassEntries, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { Combobox } from "@/components/flexkit/combobox";

export const EnvironmentClassSelector: FC<{
    selectedEnvironmentClassId: string;
    disabled: boolean;
    onSelectionChange: (entry: EnvironmentClassEntry) => void;
}> = memo(({ selectedEnvironmentClassId, onSelectionChange }) => {
    const { environmentClassEntries, isLoading: isLoadingEnvironmentClasses } = useEnvironmentClassEntries({
        enabled: true,
    });

    const selectedEnvironmentClass = useMemo(
        () => environmentClassEntries.find((it) => it.clazz.id === selectedEnvironmentClassId),
        [environmentClassEntries, selectedEnvironmentClassId],
    );

    const _onSelectionChangeById = useCallback(
        (environmentClassId: string) => {
            const entry = environmentClassEntries.find((it) => it.clazz.id === environmentClassId);
            if (entry) {
                onSelectionChange(entry);
            }
        },
        [environmentClassEntries, onSelectionChange],
    );

    useEffect(() => {
        if (selectedEnvironmentClassId && environmentClassEntries.length > 0) {
            _onSelectionChangeById(selectedEnvironmentClassId);
        }
    }, [selectedEnvironmentClassId, _onSelectionChangeById, environmentClassEntries]);

    let comboboxContent: React.ReactNode;
    if (isLoadingEnvironmentClasses) {
        comboboxContent = <Combobox.Loading>Loading environment classes…</Combobox.Loading>;
    } else {
        comboboxContent = (
            <Combobox.List
                items={environmentClassEntries}
                searchKeys={["runner.name", "clazz.displayName", "clazz.description"]}
                noMatchesComponent={<Combobox.Empty>No matching environment classes.</Combobox.Empty>}
            >
                {(entry) => <EnvironmentClassSelectItem key={entry.clazz.id} entry={entry} />}
            </Combobox.List>
        );
    }

    return (
        <Combobox
            value={selectedEnvironmentClass?.clazz.id ?? ""}
            onValueChange={_onSelectionChangeById}
            loading={isLoadingEnvironmentClasses}
        >
            <Combobox.Value>
                {selectedEnvironmentClass ? (
                    <Combobox.ValueLabel>
                        {selectedEnvironmentClass.runner.name} / {selectedEnvironmentClass.clazz.displayName}
                    </Combobox.ValueLabel>
                ) : (
                    <Combobox.ValueLabel className="text-content-muted">
                        Select an environment class
                    </Combobox.ValueLabel>
                )}
            </Combobox.Value>
            <Combobox.Popover className="w-128 max-w-[calc(100vw-1rem)]">{comboboxContent}</Combobox.Popover>
        </Combobox>
    );
});
EnvironmentClassSelector.displayName = "EnvironmentClassSelector";

const EnvironmentClassSelectItem: FC<{
    entry: EnvironmentClassEntry;
}> = memo(({ entry }) => {
    return (
        <Combobox.ListItem value={entry.clazz.id}>
            <Combobox.ListItemTitle>{entry.runner.name}</Combobox.ListItemTitle>
            <Combobox.ListItemDescription>
                {entry.clazz.displayName} • {entry.clazz.description}
            </Combobox.ListItemDescription>
        </Combobox.ListItem>
    );
});
EnvironmentClassSelectItem.displayName = "EnvironmentClassSelectItem";

export const RepoFilteringContent: FC<{
    repoSearchString: string;
    runnerId: string;
    disabled: boolean;
    scmHost: string;
    onScmHostChange: (scmHost: string) => void;
    onAddFilter: (filter: string) => void;
    onClearFilter: () => void;
    onSearchComplete?: (repos: { url: string }[]) => void;
}> = memo(
    ({
        repoSearchString,
        runnerId,
        scmHost: selectedScmHost,
        onAddFilter,
        onClearFilter,
        onScmHostChange,
        onSearchComplete,
    }) => {
        const {
            data: scmIntegrations,
            isLoading: isLoadingSCMIntegrations,
            error: listSCMIntegrationsError,
            refetch: refetchSCMIntegrations,
        } = useListRunnerSCMIntegrations(runnerId);

        let scmHost: string;
        let scmId: string;
        if (scmIntegrations && scmIntegrations.length > 0) {
            // find the SCM integration that matches the selected SCM host
            const selected = scmIntegrations.find((it) => it.host === selectedScmHost);
            if (selected) {
                scmHost = selected.host;
                scmId = selected.scmId;
            } else {
                // if the selected SCM host is not found in the list of SCM integrations, use the first integration
                scmHost = scmIntegrations[0].host;
                scmId = scmIntegrations[0].scmId;
            }
        } else {
            scmHost = "";
            scmId = "";
        }

        const {
            data: repositories,
            isLoading: isSearching,
            error: searchRepositoriesError,
            refetch: refetchRepositories,
        } = useSearchRepositoriesQuery({
            runnerId,
            query: repoSearchString,
            limit: 100,
            scmHost,
            enabled: scmHost !== "" && repoSearchString !== "",
            searchMode: SearchMode.NATIVE,
        });

        useEffect(() => {
            if (scmIntegrations && scmIntegrations.length > 0 && !selectedScmHost) {
                // if the selected SCM host is not set, use the first scm integration found for the runner
                onScmHostChange(scmIntegrations[0].host);
            }
        }, [scmIntegrations, onScmHostChange, selectedScmHost]);

        // Notify parent when search results change
        useEffect(() => {
            if (onSearchComplete && repositories && repositories.length > 0) {
                onSearchComplete(repositories.map((r) => ({ url: r.url })));
            }
        }, [repositories, onSearchComplete]);

        if (isLoadingSCMIntegrations) {
            return (
                <Text className="animate-pulse text-base text-content-secondary">
                    Finding suitable repository provider…
                </Text>
            );
        }

        if (listSCMIntegrationsError) {
            return (
                <div className="flex flex-row items-baseline gap-2">
                    <ErrorMessage error={listSCMIntegrationsError} />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            void refetchSCMIntegrations();
                        }}
                        data-tracking-id="retry"
                    >
                        Retry
                    </Button>
                </div>
            );
        }

        if (isSearching) {
            return <Text className="animate-pulse text-base text-content-secondary">Searching repositories…</Text>;
        }

        if (repositories) {
            return (
                <RepoFilterResult searchString={repoSearchString} repositories={repositories} onClear={onClearFilter} />
            );
        }

        if (scmIntegrations && scmIntegrations.length === 0) {
            return (
                <Text>The selected runner does not support repository trigger. Please select a different runner.</Text>
            );
        }

        return (
            <RepoFilterInput
                initialSearchString={repoSearchString}
                loading={isSearching}
                onAddFilter={onAddFilter}
                error={searchRepositoriesError}
                onRetry={refetchRepositories}
                scmId={scmId}
            />
        );
    },
);
RepoFilteringContent.displayName = "RepoFilteringContent";

const RepoFilterInput: FC<{
    initialSearchString: string;
    loading: boolean;
    onAddFilter: (filter: string) => void;
    error?: Error | null;
    onRetry?: () => void;
    scmId?: string;
}> = memo(({ initialSearchString, loading, onAddFilter, error, onRetry, scmId }) => {
    const [inputValue, setInputValue] = useState(initialSearchString);
    const [showError, setShowError] = useState(!!error);

    useEffect(() => {
        setShowError(!!error);
    }, [error]);

    const submitSearchString = () => {
        const value = inputValue.trim();
        if (!value) return;
        if (error && value === initialSearchString && onRetry) {
            // Same search string and there was an error - retry the query
            onRetry();
        } else {
            onAddFilter(value);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            submitSearchString();
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        if (showError) {
            setShowError(false);
        }
    };

    const scmDisplayName = scmId ? getScmDisplayName(scmId) : "repository";
    const placeholderText =
        scmDisplayName === "repository"
            ? "Describe what repositories to include or paste a filter"
            : `Describe what repositories to include or paste a ${scmDisplayName} filter`;
    const hasSearchableContent = inputValue.trim().length > 0;

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex w-full flex-row items-center gap-2">
                <Input
                    value={inputValue}
                    type="text"
                    placeholder={placeholderText}
                    className="h-9 max-w-none flex-1 bg-transparent text-content-primary focus:outline-none focus:ring-0"
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                />
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="aspect-square p-0"
                    loading={loading}
                    disabled={!hasSearchableContent}
                    LeadingIcon={withGitpodSize(CornerDownLeft)}
                    onClick={submitSearchString}
                    data-tracking-id="submitsearchstring"
                >
                    <span className="sr-only">Add repository search filter</span>
                </Button>
            </div>
            {showError && error && <ErrorMessage error={error} />}
        </div>
    );
});
RepoFilterInput.displayName = "RepoFilterInput";

const RepoFilterResult: FC<{
    searchString: string;
    repositories: PlainMessage<SearchRepositoriesResponse_Repository>[];
    onClear: () => void;
}> = memo(({ searchString, repositories, onClear }) => {
    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center gap-2">
                <Pill variant="brand" size="md">
                    {searchString}
                </Pill>
                <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={onClear}
                    data-tracking-id="code-context-repo-search-clear"
                >
                    Clear
                </Button>
            </div>
            {/* Read-only preview of matching repositories */}
            <div className="flex flex-col gap-2">
                <Text className="text-sm text-content-secondary">{repositories.length} matching repositories</Text>
                <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                    {repositories.map((repo) => (
                        <div
                            key={repo.url}
                            className="flex flex-row items-center gap-2 rounded bg-surface-secondary px-2 py-1"
                        >
                            <Text className="flex-1 truncate text-sm">{repo.url}</Text>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
});
RepoFilterResult.displayName = "RepoFilterResult";
